import { useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useValidationFormatter, RuleType } from "../../input/validation.js";
import "../../input/DatePicker.js";
import "../../input/DateRangePicker.js";
import "../../input/StyledInput.js";
import "../../input/FormInput.js";
import "../../input/StyledFormInputHoc.js";
import "moment-timezone";
import "../../input/DateSinglePicker.localize.js";
import "../../input/hooks.js";
import "@mui/material";
import { localized as localized$1 } from "./RelativeRange.js";
const localized = defineMessages({
  anyValue: {
    id: "ValueRangeFilter_anyValue",
    defaultMessage: "Any"
  },
  selectBoxBetween: {
    id: "ValueRangeFilter_selectBoxBetween",
    defaultMessage: "between: {from}-{to}"
  },
  selectBoxEquals: {
    id: "ValueRangeFilter_selectBoxEquals",
    defaultMessage: "equals: {from}"
  },
  selectBoxIsGreaterThan: {
    id: "ValueRangeFilter_selectBoxIsGreaterThan",
    defaultMessage: "is greater than: {from}"
  },
  selectBoxIsLessThan: {
    id: "ValueRangeFilter_selectBoxIsLessThan",
    defaultMessage: "is less than: {to}"
  }
});
const useSelectBoxValue = (model) => {
  const { formatMessage } = useIntl();
  if (isNaN(model?.value?.from) && isNaN(model?.value?.to)) return formatMessage(localized.anyValue);
  const {
    operator,
    value: { from, to }
  } = model;
  const messageMapper = {
    between: formatMessage(localized.selectBoxBetween, { from, to }),
    equals: formatMessage(localized.selectBoxEquals, { from }),
    less: formatMessage(localized.selectBoxIsLessThan, { to }),
    greater: formatMessage(localized.selectBoxIsGreaterThan, { from })
  };
  const formatted = messageMapper[operator];
  return formatted ?? formatMessage(localized.anyValue);
};
function useValueRangeValidation({ min, max, operator }) {
  const validationFormatter = useValidationFormatter();
  const fromLocalized = localized$1.firstValueRangeInput;
  const toLocalized = localized$1.secondValueRangeInput;
  const fromRequired = useCallback(
    (value) => !isNaN(value.from) ? true : validationFormatter(RuleType.Required, fromLocalized),
    []
  );
  const fromInteger = useCallback(
    (value) => typeof value.from === "number" && Number.isInteger(value.from) ? true : validationFormatter(RuleType.Integer, fromLocalized),
    []
  );
  const fromMinValue = useCallback(
    (value) => isNaN(min) || value.from >= min ? true : validationFormatter(RuleType.Min, fromLocalized, min),
    [min]
  );
  const fromMaxValue = useCallback(
    (value) => {
      const maxAmount = operator === "between" ? value.to : max;
      return isNaN(maxAmount) || value.from <= maxAmount ? true : validationFormatter(RuleType.Max, fromLocalized, maxAmount);
    },
    [max, operator]
  );
  const toRequired = useCallback(
    (value) => !isNaN(value.to) ? true : validationFormatter(RuleType.Required, toLocalized),
    []
  );
  const toInteger = useCallback(
    (value) => typeof value.to === "number" && Number.isInteger(value.to) ? true : validationFormatter(RuleType.Integer, toLocalized),
    []
  );
  const toMinValue = useCallback(
    (value) => {
      const minAmount = operator === "between" ? value.from : min;
      return isNaN(minAmount) || value.to >= minAmount ? true : validationFormatter(RuleType.Min, toLocalized, minAmount);
    },
    [min, operator]
  );
  const toMaxValue = useCallback(
    (value) => isNaN(max) || value.to <= max ? true : validationFormatter(RuleType.Max, toLocalized, max),
    [max]
  );
  const greaterAndEqualValidation = {
    fromRequired,
    fromInteger,
    fromMinValue,
    fromMaxValue
  };
  const lessValidation = {
    toRequired,
    toInteger,
    toMinValue,
    toMaxValue
  };
  const betweenValidation = {
    fromRequired,
    fromInteger,
    toRequired,
    toInteger,
    fromMinValue,
    fromMaxValue,
    toMinValue,
    toMaxValue
  };
  const validationMapping = {
    equals: greaterAndEqualValidation,
    less: lessValidation,
    greater: greaterAndEqualValidation,
    between: betweenValidation
  };
  return validationMapping[operator];
}
export {
  localized,
  useSelectBoxValue,
  useValueRangeValidation
};
//# sourceMappingURL=hooks.js.map
